import { useCallback, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { ILockableEntity, ISignableEntity } from "../../../api/GenericTypes";
import { Button } from "../../../common/buttons/Button/Button";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { useEntityApi } from "../../../api/useEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";

export interface GenericEntityLockButtonProps<Entity = GenericEntity> {
  btnClass?: string;
  hideLabel?: boolean;
  onSuccess?: (data: Entity) => void;
  onClickCallback?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
}

export const GenericEntityLockButton = <
  Entity extends GenericEntity & Partial<ILockableEntity> & Partial<ISignableEntity>,
  ComponentProps extends GenericEntityLockButtonProps<Entity>
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { lockMutationAsync, isLoadingLockMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const onSuccessCallback = useCallback(
    (data: Entity) => {
      componentProps?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
    },
    [componentProps, entityConstants.resource, queryClient]
  );

  const handleLock = useCallback(async () => {
    if (!entity?.id) return;
    await lockMutationAsync({ id: entity.id }, { onSuccess: onSuccessCallback }).catch((e) => console.error(e));
    setShowModal(false);
  }, [entity?.id, lockMutationAsync, onSuccessCallback]);

  const { canLock } = useEntityPermissions({ entityTypeId });

  if (!entity || !entityConstants || !!entity.isLocked || !!entity.isSigned) return null;
  return (
    <>
      <Button
        onClick={(e) => {
          setShowModal(true);
          componentProps?.onClickCallback?.(e);
        }}
        loading={isLoadingLockMutation}
        disabled={!canLock(entity)}
        className={componentProps?.btnClass ?? "btn btn-block btn-sm btn-ghost-info flex align-center gap-5"}
      >
        <LucideIcon name="lock" />
        {!componentProps?.hideLabel && "Lock"}
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="warning"
          onProceed={handleLock}
          onCancel={() => setShowModal(false)}
          title="Lock content"
          description="Are you sure you want to lock this content? This will prevent other users from editing this content."
          proceedLabel="Lock"
          iconOverride="lock"
        />
      )}
    </>
  );
};
